import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { examComponentsAPI } from '../../services/api';
import './Exams.css';

const EXAM_EVENT_TYPES = new Set([
  'exam',
  'resit',
  'bp-submission',
  'so-submission',
  'adviesrapport-submission',
  'presentation',
  // Legacy values still accepted for previously stored calendar events.
  'bp-so-submission',
  'report-submission',
  'report-review'
]);

const examMomentTypeLabel = (value) => {
  const labels = {
    kennistentamen: 'Kennistentamen',
    'onderzoeksverslag-voorstel': 'Onderzoeksverslag/Voorstel',
    beroepsproduct: 'Beroepsproduct',
    adviesrapport: 'Adviesrapport',
    'reflectieverslag-portfolio': 'Reflectieverslag/Portfolio',
    'schriftelijke-opdracht': 'Schriftelijke opdracht',
    presentatievorm: 'Presentatievorm'
  };
  return labels[value] || value || '-';
};

const Exams = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { getStudentGrades, getStudentEnrollments, data } = useData();
  const [examComponents, setExamComponents] = useState([]);
  const [examUploading, setExamUploading] = useState(false);
  const [selectedExamFiles, setSelectedExamFiles] = useState({});
  const [examComments, setExamComments] = useState({});

  const userId = user?.id || user?._id;
  const grades = getStudentGrades(userId);

  const enrollments = userId ? (getStudentEnrollments(userId) || []) : [];
  const enrolledCourseIds = new Set(
    enrollments
      .filter((enrollment) => enrollment.status !== 'cancelled')
      .map((enrollment) => String(enrollment.courseId || enrollment.course?.id || enrollment.course?._id || enrollment.course))
  );

  const courseExamSchedule = (data?.schedules || [])
    .filter((event) => {
      const eventCourseId = String(event.courseId || event.course?.id || event.course?._id || event.course || '');
      const eventType = String(event.eventType || event.type || '');
      return enrolledCourseIds.has(eventCourseId) && EXAM_EVENT_TYPES.has(eventType);
    })
    .sort((a, b) => new Date(a.startDateTime || a.start || 0) - new Date(b.startDateTime || b.start || 0));

  const formatDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const fetchExamComponents = async () => {
    try {
      const response = await examComponentsAPI.getStudentPending();
      setExamComponents(response.items || []);
    } catch (error) {
      console.error('Load exam component uploads', error);
    }
  };

  useEffect(() => {
    if (user) {
      fetchExamComponents();
    }
  }, [user]);

  const handleExamComponentSubmit = async (item) => {
    const scheduleEventId = String(item.scheduleEventId || '');
    const file = selectedExamFiles[scheduleEventId];
    if (!file) return;

    setExamUploading(true);
    try {
      await examComponentsAPI.submitStudentFile({
        scheduleEventId,
        file,
        comments: examComments[scheduleEventId] || ''
      });
      setSelectedExamFiles((prev) => ({ ...prev, [scheduleEventId]: null }));
      setExamComments((prev) => ({ ...prev, [scheduleEventId]: '' }));
      await fetchExamComponents();
    } catch (error) {
      console.error('Submit exam component', error);
      alert(error.message || t('common.error'));
    } finally {
      setExamUploading(false);
    }
  };

  const assessments = grades.map(g => ({
    id: g.id,
    examType: g.examType || 'other',
    course: g.course,
    score: g.score,
    grade: g.grade,
    status: g.status,
    date: g.gradeDate
  }));

  return (
    <div className="exams-page">
      <div className="page-header">
        <h1>{t('nav.exams')}</h1>
      </div>

      <div className="exams-list">
        <h2>Examens van vakken</h2>
        {courseExamSchedule.length > 0 ? (
          courseExamSchedule.map((event) => {
            const grade = grades.find((g) =>
              g.examType === event.examType &&
              String(g.courseId || g.course?.id || g.course?._id || g.course || '') === String(event.courseId || event.course?.id || event.course?._id || event.course || '')
            );

            return (
              <div key={event.id} className="exam-card">
                <div className="exam-header">
                  <h3>{event.courseCode ? `${event.courseCode} - ${event.courseName}` : (event.courseName || event.title)}</h3>
                  <span className="exam-type">{examMomentTypeLabel(event.examType)}</span>
                </div>
                <div className="exam-details">
                  <div className="detail-row">
                    <span className="detail-label">Kalender item</span>
                    <span className="detail-value">{event.title || '-'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Datum</span>
                    <span className="detail-value">{formatDateTime(event.startDateTime || event.start)}</span>
                  </div>
                  {event.location && (
                    <div className="detail-row">
                      <span className="detail-label">Locatie</span>
                      <span className="detail-value">{event.location}</span>
                    </div>
                  )}
                </div>
                {grade && (
                  <div className="exam-details">
                    <div className="detail-row">
                      <span className="detail-label">{t('grade.grade')}</span>
                      <span className="detail-value">{grade.score}% - {t(`grade.${grade.status}`)}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <p>{t('common.noData')}</p>
        )}
      </div>

      <div className="exams-list">
        {examComponents.map((item) => {
          const scheduleEventId = String(item.scheduleEventId);
          const alreadySubmitted = Boolean(item.submitted);
          const canUploadNow = Boolean(item.canUploadNow ?? item.canUploadToday) && !alreadySubmitted;
          const examTypeLabel = examMomentTypeLabel(item.examType);
          const uploadAvailableFrom = item.uploadAvailableFrom || item.createdAt;
          const uploadAvailableUntil = item.uploadAvailableUntil || item.endDateTime;
          const uploadStatusLabel = alreadySubmitted
            ? 'Geupload'
            : (canUploadNow ? 'Nu uploaden' : (item.uploadWindowStatus === 'scheduled' ? 'Nog niet beschikbaar' : 'Deadline verstreken'));

          return (
            <div key={scheduleEventId} className="exam-card">
              <div className="exam-header">
                <h3>{examTypeLabel}</h3>
                <span className="exam-type">{uploadStatusLabel}</span>
              </div>

              <div className="exam-details">
                <div className="detail-row">
                  <span className="detail-label">Vak</span>
                  <span className="detail-value">{item.course?.code ? `${item.course.code} - ${item.course.name}` : (item.course?.name || '-')}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Geplande examendag</span>
                  <span className="detail-value">{formatDateTime(item.startDateTime)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Kalender item</span>
                  <span className="detail-value">{item.title || '-'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Uploadperiode</span>
                  <span className="detail-value">
                    {formatDateTime(uploadAvailableFrom)} t/m {formatDateTime(uploadAvailableUntil)}
                  </span>
                </div>
              </div>

              {alreadySubmitted && (
                <div className="detail-row">
                  <span className="detail-label">Geupload op</span>
                  <span className="detail-value">{formatDateTime(item.submitted?.submittedAt)}</span>
                  {item.submitted?.file?.url && (
                    <button
                      type="button"
                      className="attend-btn"
                      onClick={() => window.open(item.submitted.file.url, '_blank', 'noopener,noreferrer')}
                    >
                      Bekijk upload ({item.submitted.file.originalName || 'bestand'})
                    </button>
                  )}
                </div>
              )}

              {!alreadySubmitted && (
                <div className="exam-details">
                  <label className="detail-row">
                    <span className="detail-label">Bestand</span>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={(e) => setSelectedExamFiles((prev) => ({ ...prev, [scheduleEventId]: e.target.files?.[0] || null }))}
                      disabled={!canUploadNow}
                    />
                  </label>

                  <label className="detail-row">
                    <span className="detail-label">Opmerking (optioneel)</span>
                    <textarea
                      value={examComments[scheduleEventId] || ''}
                      onChange={(e) => setExamComments((prev) => ({ ...prev, [scheduleEventId]: e.target.value }))}
                      disabled={!canUploadNow}
                    />
                  </label>

                  <div className="detail-row">
                    <button
                      type="button"
                      className="attend-btn"
                      onClick={() => handleExamComponentSubmit(item)}
                      disabled={examUploading || !canUploadNow || !selectedExamFiles[scheduleEventId]}
                    >
                      {examUploading ? t('common.loading') : 'Upload examenonderdeel'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="exams-list">
        {assessments.map((exam) => {
          return (
            <div key={exam.id} className="exam-card">
              <div className="exam-header">
                <h3>{exam.course?.name || '-'}</h3>
                <span className="exam-type">
                  {t(`exam.${exam.examType}`)}
                </span>
              </div>
              
              <div className="exam-course">
                <span className="course-name">{exam.course?.code || '-'}</span>
                <span className="course-code">{exam.date ? new Date(exam.date).toLocaleDateString() : '-'}</span>
              </div>

              <div className="exam-details">
                <div className="detail-row">
                  <span className="detail-label">{t('grade.grade')}:</span>
                  <span className="detail-value">{typeof exam.grade === 'number' ? `${exam.grade.toFixed(1)}/10` : '-'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">{t('grade.score')}:</span>
                  <span className="detail-value">{typeof exam.score === 'number' ? `${exam.score}%` : '-'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">{t('common.status')}:</span>
                  <span className="detail-value">{t(`grade.${exam.status}`)}</span>
                </div>
              </div>
            </div>
          );
        })}
        
        {assessments.length === 0 && (
          <div className="no-exams">
            <p>{t('common.noData')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Exams;
